import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { Zap, Mail, Lock, ArrowLeft, Store, MapPin, Phone, Eye, EyeOff } from 'lucide-react';

const ShopRegister = () => {
  const navigate = useNavigate();
  const { registerShop, verifyShop } = useAuth();
  const [step, setStep] = useState(1); // 1 = register, 2 = verify
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  const [formData, setFormData] = useState({
    nome_negozio: '',
    email: '',
    password: '',
    indirizzo: '',
    telefono: ''
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await registerShop(formData);
      setVerificationEmail(formData.email);
      setStep(2);
      toast.success('Codice di verifica inviato a registrazionenegozio@tagliandielectrizz.it!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la registrazione';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await verifyShop(verificationEmail, verificationCode);
      toast.success('Negozio verificato con successo!');
      navigate('/shop/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Codice non valido';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div className="hidden lg:block relative">
        <img 
          src="https://images.unsplash.com/photo-1545732870-5dced7323d26?w=1200&h=1600&fit=crop"
          alt="Workshop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <Store className="w-16 h-16 text-primary mb-6" />
            <h1 className="font-heading text-4xl font-black mb-4">Registra il Tuo Negozio</h1>
            <p className="text-white/80 text-lg">
              Unisciti alla rete NIU Service e gestisci i tagliandi in modo efficiente.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>

          {step === 1 ? (
            <Card className="border-border/50">
              <CardHeader className="space-y-1">
                <div className="flex items-center gap-2 lg:hidden mb-4">
                  <Zap className="w-8 h-8 text-primary" />
                  <span className="font-heading font-bold text-xl">NIU Service</span>
                </div>
                <CardTitle className="font-heading text-2xl">Registra Negozio</CardTitle>
                <CardDescription>
                  Inserisci i dati del tuo negozio per iniziare
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome_negozio">Nome Negozio *</Label>
                    <div className="relative">
                      <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="nome_negozio"
                        placeholder="NIU Center Milano"
                        className="pl-10 h-12"
                        value={formData.nome_negozio}
                        onChange={(e) => setFormData({ ...formData, nome_negozio: e.target.value })}
                        required
                        data-testid="shop-register-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Negozio *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="negozio@esempio.com"
                        className="pl-10 h-12"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="shop-register-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10 h-12"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                        data-testid="shop-register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="indirizzo">Indirizzo *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="indirizzo"
                        placeholder="Via Roma 123, Milano"
                        className="pl-10 h-12"
                        value={formData.indirizzo}
                        onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                        required
                        data-testid="shop-register-address-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telefono">Telefono *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="telefono"
                        type="tel"
                        placeholder="+39 02 1234567"
                        className="pl-10 h-12"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        required
                        data-testid="shop-register-phone-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 font-bold"
                    disabled={isLoading}
                    data-testid="shop-register-submit-btn"
                  >
                    {isLoading ? 'Registrazione in corso...' : 'Registra Negozio'}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm">
                  <span className="text-muted-foreground">Hai già un account? </span>
                  <Link to="/shop/login" className="text-primary font-medium hover:underline" data-testid="go-to-shop-login-link">
                    Accedi
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/50">
              <CardHeader className="space-y-1">
                <div className="flex items-center gap-2 lg:hidden mb-4">
                  <Zap className="w-8 h-8 text-primary" />
                  <span className="font-heading font-bold text-xl">NIU Service</span>
                </div>
                <CardTitle className="font-heading text-2xl">Verifica Email</CardTitle>
                <CardDescription>
                  Inserisci il codice a 6 cifre inviato a registrazionenegozio@tagliandielectrizz.it per autorizzare {verificationEmail}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerify} className="space-y-6">
                  <div className="flex justify-center">
                    <InputOTP 
                      maxLength={6} 
                      value={verificationCode}
                      onChange={setVerificationCode}
                      data-testid="shop-verify-code-input"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 font-bold"
                    disabled={isLoading || verificationCode.length !== 6}
                    data-testid="shop-verify-submit-btn"
                  >
                    {isLoading ? 'Verifica in corso...' : 'Verifica Negozio'}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm">
                  <button 
                    onClick={() => setStep(1)} 
                    className="text-primary font-medium hover:underline"
                    data-testid="go-back-to-register-btn"
                  >
                    Torna indietro
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopRegister;
